'use client';
import React, { useEffect, useState, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import StatsBar from '@/components/StatsBar';
import GlobalCounter from '@/components/GlobalCounter';
import MuteToggle from '@/components/MuteToggle';
import AppLogo from '@/components/ui/AppLogo';
import { playTap, haptic } from '@/lib/audioEngine';
import { getMuted } from '@/lib/gameStore';

const headlines = [
  'Bored? Hit the button.',
  'One button. Infinite dumb fun.',
  'How long can you go?',
];

// Separate component so useSearchParams is inside a Suspense boundary
function ReferralCapture({ onRef }: { onRef: (code: string) => void }) {
  const searchParams = useSearchParams();
  useEffect(() => {
    const ref = searchParams?.get('ref');
    if (ref) {
      try {
        sessionStorage.setItem('pending_referral_code', ref.toUpperCase());
        onRef(ref.toUpperCase());
      } catch {
        // sessionStorage not available
      }
    }
  }, [searchParams, onRef]);
  return null;
}

export default function HomeHero() {
  const router = useRouter();
  const [headlineIdx, setHeadlineIdx] = useState(0);
  const [pressing, setPressing] = useState(false);
  const [launched, setLaunched] = useState(false);
  const [refBanner, setRefBanner] = useState<string | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setHeadlineIdx(i => (i + 1) % headlines?.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  const handleTap = () => {
    if (launched) return;
    setPressing(true);
    if (!getMuted()) playTap();
    haptic(30);
    setTimeout(() => {
      setLaunched(true);
      router?.push('/game-stage');
    }, 200);
  };

  return (
    <div className="relative z-10 flex flex-col min-h-screen">
      {/* Referral code capture — wrapped in Suspense per Next.js requirement */}
      <Suspense fallback={null}>
        <ReferralCapture onRef={setRefBanner} />
      </Suspense>

      {/* Referral welcome banner */}
      {refBanner && (
        <div
          className="mx-4 mt-3 rounded-xl px-4 py-2.5 flex items-center gap-3"
          style={{
            background: 'linear-gradient(135deg, rgba(255,210,63,0.12), rgba(255,149,0,0.08))',
            border: '1px solid rgba(255,210,63,0.35)',
          }}
        >
          <span className="text-lg">🎁</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-black text-foreground leading-tight">You were invited!</p>
            <p className="text-xs text-muted-foreground font-medium">Sign up and your friend earns 7 free premium days.</p>
          </div>
          <button
            onClick={() => setRefBanner(null)}
            className="text-xs text-muted-foreground flex-shrink-0"
            style={{ opacity: 0.5 }}
          >
            ✕
          </button>
        </div>
      )}
      {/* Header */}
      <header className="flex items-center justify-between px-5 pt-5 pb-3">
        <div className="flex items-center gap-2">
          <AppLogo size={32} />
          <span className="font-bold text-lg tracking-tight text-foreground">Unbored</span>
        </div>
        <div className="flex items-center gap-2">
          <StatsBar />
          <MuteToggle />
        </div>
      </header>
      {/* Main hero */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 gap-8">
        {/* Headline */}
        <div className="text-center max-w-sm">
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight text-foreground">
            {headlines?.[headlineIdx]}
          </h1>
          <p className="mt-3 text-muted-foreground text-base font-medium">
            Random micro-games. No redirects. No ads. Just play.
          </p>
        </div>

        {/* THE BUTTON */}
        <div className="relative flex items-center justify-center my-4">
          {/* Outer ring pulse */}
          <div className="absolute w-72 h-72 rounded-full opacity-20 pulse-anim"
            style={{ background: 'radial-gradient(circle, #ff3b6b 0%, transparent 65%)' }} />
          <div className="absolute w-56 h-56 rounded-full opacity-30 pulse-anim"
            style={{ background: 'radial-gradient(circle, #7b5bff 0%, transparent 65%)', animationDelay: '1s' }} />

          <button
            onClick={handleTap}
            disabled={launched}
            className="relative w-52 h-52 rounded-full font-extrabold text-2xl text-white press-down z-10"
            style={{
              background: pressing
                ? 'linear-gradient(135deg, #c4205a 0%, #5a3fd4 100%)'
                : 'linear-gradient(135deg, #ff3b6b 0%, #7b5bff 100%)',
              boxShadow: pressing
                ? '0 4px 20px rgba(255,59,107,0.3)'
                : '0 0 60px rgba(255,59,107,0.5), 0 0 100px rgba(123,91,255,0.3)',
              transition: 'all 150ms ease',
              transform: pressing ? 'scale(0.93) translateY(6px)' : 'scale(1)',
              border: '4px solid rgba(255,255,255,0.15)',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
            }}
            aria-label="Tap to play a random game"
          >
            <span className="block text-5xl mb-1">👆</span>
            <span className="block text-xl font-black tracking-wider">TAP ME</span>
          </button>
        </div>

        {/* Global counter */}
        <GlobalCounter />

        {/* Feature pills */}
        <div className="flex flex-wrap gap-2 justify-center max-w-sm">
          {[
            { icon: '⚡', label: '10 free games' },
            { icon: '🔥', label: 'Build your streak' },
            { icon: '📱', label: 'Plays in-browser' },
            { icon: '🏆', label: 'Daily challenge' },
          ]?.map((f) => (
            <span key={`feature-${f?.label}`} className="stat-pill text-xs">
              <span>{f?.icon}</span>
              <span className="text-muted-foreground">{f?.label}</span>
            </span>
          ))}
        </div>

        {/* Daily challenge CTA */}
        <div
          className="w-full max-w-sm rounded-2xl p-4 flex items-center justify-between cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, rgba(255,210,63,0.08), rgba(255,210,63,0.04))',
            border: '1px solid rgba(255,210,63,0.3)',
          }}
          onClick={() => router?.push('/daily-challenge-leaderboard')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e?.key === 'Enter' && router?.push('/daily-challenge-leaderboard')}
        >
          <div>
            <div className="daily-badge mb-1.5">Daily Challenge</div>
            <p className="text-sm font-bold text-foreground">Can you top today&apos;s leaderboard?</p>
            <p className="text-xs text-muted-foreground mt-0.5">Resets midnight UTC</p>
          </div>
          <span className="text-2xl">🏆</span>
        </div>

        {/* Useless Web CTA */}
        <div
          className="w-full max-w-sm rounded-2xl p-4 flex items-center justify-between cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, rgba(123,91,255,0.08), rgba(255,59,107,0.04))',
            border: '1px solid rgba(123,91,255,0.3)',
          }}
          onClick={() => router?.push('/useless-web')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e?.key === 'Enter' && router?.push('/useless-web')}
        >
          <div>
            <div className="text-xs font-bold uppercase tracking-widest mb-1.5"
              style={{ color: '#7b5bff' }}>
              🌐 Useless Web
            </div>
            <p className="text-sm font-bold text-foreground">10 pointless in-browser experiences</p>
            <p className="text-xs text-muted-foreground mt-0.5">Mondrian art, Matrix rain, Nyan Cat &amp; more</p>
          </div>
          <span className="text-2xl">🎨</span>
        </div>

        {/* Premium CTA */}
        <div
          className="w-full max-w-sm rounded-2xl p-4 flex items-center justify-between cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, rgba(255,210,63,0.1), rgba(255,149,0,0.05))',
            border: '1px solid rgba(255,210,63,0.35)',
            boxShadow: '0 0 20px rgba(255,210,63,0.06)',
          }}
          onClick={() => router?.push('/premium')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e?.key === 'Enter' && router?.push('/premium')}
        >
          <div>
            <div
              className="text-xs font-black uppercase tracking-widest mb-1.5"
              style={{ color: '#ffd23f' }}
            >
              ✦ Premium
            </div>
            <p className="text-sm font-bold text-foreground">3 exclusive games — locked 🔒</p>
            <p className="text-xs text-muted-foreground mt-0.5">$4.99/mo · cancel anytime</p>
          </div>
          <span className="text-2xl">👑</span>
        </div>

        {/* Teams CTA */}
        <div
          className="w-full max-w-sm rounded-2xl p-4 flex items-center justify-between cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, rgba(54,226,164,0.1), rgba(123,91,255,0.06))',
            border: '1px solid rgba(54,226,164,0.35)',
            boxShadow: '0 0 20px rgba(54,226,164,0.06)',
          }}
          onClick={() => router?.push('/teams')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e?.key === 'Enter' && router?.push('/teams')}
        >
          <div>
            <div
              className="text-xs font-black uppercase tracking-widest mb-1.5"
              style={{ color: '#36e2a4' }}
            >
              🏢 For Teams
            </div>
            <p className="text-sm font-bold text-foreground">Brain breaks for remote teams</p>
            <p className="text-xs text-muted-foreground mt-0.5">$49/mo · 25 seats · shared streaks</p>
          </div>
          <span className="text-2xl">🤝</span>
        </div>

        {/* Arena CTA */}
        <div
          className="w-full max-w-sm rounded-2xl p-4 flex items-center justify-between cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, rgba(255,59,107,0.1), rgba(123,91,255,0.06))',
            border: '1px solid rgba(255,59,107,0.3)',
            boxShadow: '0 0 20px rgba(255,59,107,0.06)',
          }}
          onClick={() => router?.push('/arena')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e?.key === 'Enter' && router?.push('/arena')}
        >
          <div>
            <div
              className="text-xs font-black uppercase tracking-widest mb-1.5"
              style={{ color: '#ff3b6b' }}
            >
              ⚔️ Arena
            </div>
            <p className="text-sm font-bold text-foreground">Challenge a friend — live duel</p>
            <p className="text-xs text-muted-foreground mt-0.5">Real-time · same game · winner takes all</p>
          </div>
          <span className="text-2xl">🏆</span>
        </div>

        {/* Profile CTA */}
        <div
          className="w-full max-w-sm rounded-2xl p-4 flex items-center justify-between cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, rgba(54,226,164,0.08), rgba(123,91,255,0.04))',
            border: '1px solid rgba(54,226,164,0.25)',
            boxShadow: '0 0 20px rgba(54,226,164,0.04)',
          }}
          onClick={() => router?.push('/profile')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e?.key === 'Enter' && router?.push('/profile')}
        >
          <div>
            <div
              className="text-xs font-black uppercase tracking-widest mb-1.5"
              style={{ color: '#36e2a4' }}
            >
              👤 My Profile
            </div>
            <p className="text-sm font-bold text-foreground">Stats, rank &amp; account settings</p>
            <p className="text-xs text-muted-foreground mt-0.5">Kills · streak · arena rank · premium</p>
          </div>
          <span className="text-2xl">📊</span>
        </div>

        {/* Leaderboard CTA */}
        <div
          className="w-full max-w-sm rounded-2xl p-4 flex items-center justify-between cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, rgba(255,59,107,0.07), rgba(255,210,63,0.04))',
            border: '1px solid rgba(255,210,63,0.25)',
            boxShadow: '0 0 20px rgba(255,210,63,0.04)',
          }}
          onClick={() => router?.push('/leaderboard')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e?.key === 'Enter' && router?.push('/leaderboard')}
        >
          <div>
            <div
              className="text-xs font-black uppercase tracking-widest mb-1.5"
              style={{ color: '#ffd23f' }}
            >
              🌍 Global Leaderboard
            </div>
            <p className="text-sm font-bold text-foreground">All-time rankings · live sync</p>
            <p className="text-xs text-muted-foreground mt-0.5">Arena wins · best streak · lifetime kills</p>
          </div>
          <span className="text-2xl">🏅</span>
        </div>

        {/* Intro / Boredom Scenarios Section */}
        <div className="w-full max-w-sm mt-4 rounded-2xl p-5"
          style={{ background: 'rgba(123,91,255,0.07)', border: '1px solid rgba(123,91,255,0.2)' }}>
          <h2 className="text-xl font-extrabold text-foreground mb-3">
            Go ahead, press the unBored Button™
          </h2>
          <ul className="space-y-1.5 mb-4">
            {[
              { emoji: '🏫', text: "I'm bored at school." },
              { emoji: '🏠', text: "I'm bored at home." },
              { emoji: '💼', text: "I'm bored at work." },
              { emoji: '🚗', text: "I'm bored at the DMV." },
            ]?.map((item) => (
              <li key={item?.text} className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="text-base">{item?.emoji}</span>
                <span>{item?.text}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-foreground font-medium leading-relaxed">
            Do you find yourself saying any of the above? If so, you&apos;ve reached the right website.
          </p>
        </div>

        {/* Comparison Section */}
        <div className="w-full max-w-sm rounded-2xl overflow-hidden"
          style={{ border: '1px solid rgba(255,59,107,0.2)' }}>
          <div className="px-4 py-3"
            style={{ background: 'rgba(255,59,107,0.08)' }}>
            <p className="text-xs font-bold uppercase tracking-widest text-pink-400">Why Unbored beats the rest</p>
          </div>
          <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
            {[
              { pain: '🐌 Slow external sites', fix: 'Everything runs in the page — zero redirects, instant load' },
              { pain: '🚫 Unvetted content', fix: '10 handcrafted games, 100% controlled' },
              { pain: '😶 No continuity', fix: 'Streak system + "keep going" loop creates compulsion' },
              { pain: '📢 Ad pollution', fix: 'Clean, premium dark UI — no ads ever' },
              { pain: '📵 Broken on mobile', fix: 'Mobile-first, tap-native design' },
              { pain: '🤐 Nothing to share', fix: 'Result card + Web Share API + OG image with your score' },
            ]?.map((row) => (
              <div key={row?.pain} className="px-4 py-3 grid grid-cols-2 gap-3">
                <div className="text-xs text-red-400 font-medium leading-snug">{row?.pain}</div>
                <div className="text-xs text-green-400 font-medium leading-snug">✓ {row?.fix}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Closing CTA copy */}
        <div className="w-full max-w-sm rounded-2xl p-5 text-center"
          style={{ background: 'rgba(54,226,164,0.06)', border: '1px solid rgba(54,226,164,0.2)' }}>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Clicking the big red button will instantly take you to one of hundreds of interactive experiences
            specially selected to alleviate boredom. So go ahead and give it a try.{' '}
            <span className="text-foreground font-semibold">Press the unBored Button and be bored no more.</span>
          </p>
        </div>

      </div>
    </div>
  );
}